import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { FlaskConical, ArrowRight, Sparkles, Wifi, MapPin, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import ResidencyCaseStudies from "@/components/ResidencyCaseStudies";
import AnnotatedImage from "@/components/AnnotatedImage";
import BeforeAfterAnnotated from "@/components/BeforeAfterAnnotated";

// XAUUSD 1h — March 3 2026 setup, two frames of the same trade.
const CHART_BEFORE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/rztlg3fk_Screenshot%202026-03-05%20at%2012.23.50%E2%80%AFAM.webp";
const CHART_AFTER =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/71tdp16j_Screenshot%202026-03-05%20at%205.20.51%E2%80%AFPM%202.webp";

// Small opaque rectangle to hide the leaked TradingView indicator labels
// (MA Cross, TF SL, ICT HT, M3 S…) in the AM chart's top-left corner.
// Positioned as a % of the image, so it stays put across breakpoints.
const BEFORE_MASK = [{ x: 1, y: 6.5, w: 7, h: 22, color: "#fafafa" }];

const CHART_STEPS_BEFORE = [
  { x: 78, y: 28, lx: 3,  ly: 6,   titleKey: "residencyInfoUi.chartStepsBefore1Title",       bodyKey: "residencyInfoUi.chartStepsBefore1Body" },
  { x: 88, y: 60, lx: 3,  ly: 42,  titleKey: "residencyInfoUi.chartStepsBefore2Title", bodyKey: "residencyInfoUi.chartStepsBefore2Body" },
  { x: 92, y: 85, lx: 42, ly: 72,  titleKey: "residencyInfoUi.chartStepsBefore3Title",       bodyKey: "residencyInfoUi.chartStepsBefore3Body" },
];

const CHART_STEPS_AFTER = [
  { x: 55, y: 46, lx: 3,  ly: 6,   titleKey: "residencyInfoUi.chartStepsAfter1Title",     bodyKey: "residencyInfoUi.chartStepsAfter1Body" },
  { x: 75, y: 44, lx: 3,  ly: 42,  titleKey: "residencyInfoUi.chartStepsAfter2Title",      bodyKey: "residencyInfoUi.chartStepsAfter2Body" },
  { x: 86, y: 62, lx: 42, ly: 72,  titleKey: "residencyInfoUi.chartStepsAfter3Title",     bodyKey: "residencyInfoUi.chartStepsAfter3Body" },
];

// The narrative six-day arc. Same underlying agenda, but each day reads
// as a chapter rather than a curriculum bullet.
const FORMATS = [
  { id: "bench_side", Icon: MapPin,   labelKey: "residencyInfoUi.formatBenchSideLabel",  lineKey: "residencyInfoUi.formatBenchSideLine" },
  { id: "remote_lab", Icon: Wifi,     labelKey: "residencyInfoUi.formatRemoteLabLabel",  lineKey: "residencyInfoUi.formatRemoteLabLine" },
  { id: "solo_bench", Icon: Sparkles, labelKey: "residencyInfoUi.formatSoloBenchLabel",  lineKey: "residencyInfoUi.formatSoloBenchLine" },
];

// Mirror-style inner-monologue for the "who this is for" section. The
// point is not to describe the reader — it's to make them nod and think
// "wow, me too." Short. First-person. Present-tense. Nothing to argue with.
const ME_TOO_KEYS = [
  "residencyInfoUi.meToo1",
  "residencyInfoUi.meToo2",
  "residencyInfoUi.meToo3",
  "residencyInfoUi.meToo4",
  "residencyInfoUi.meToo5",
];
const PUNCHLINE_KEY = "residencyInfoUi.meTooPunchline";

/**
 * Cinematic reveal for the "me too" mirror section.
 *
 * IntersectionObserver-triggered so the animation only fires when the
 * section actually enters the viewport — otherwise a fast-scrolling
 * visitor would miss the moment. Each italic quote fades and rises,
 * staggered 500 ms; the punchline lands 400 ms later with a heavier
 * scale + slower duration; the amber divider then grows in width; and
 * the framing line settles in last. Dark backdrop with a soft radial
 * glow keeps the tone "private confessional" rather than "hero pitch".
 */
function MeTooReveal() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOn(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const line = (delay, extra = {}) => ({
    opacity: on ? 1 : 0,
    transform: on ? "translateY(0)" : "translateY(24px)",
    filter: on ? "blur(0)" : "blur(4px)",
    transition: `opacity 900ms ease ${delay}ms, transform 900ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, filter 900ms ease ${delay}ms`,
    ...extra,
  });

  const punchDelay = 200 + ME_TOO_KEYS.length * 500;

  return (
    <section
      ref={ref}
      data-testid="residency-me-too"
      className="relative overflow-hidden bg-slate-950 text-white"
    >
      {/* Soft radial glow, very subtle */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.10),transparent_60%)]" />
      {/* Extra top/bottom vignette */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-slate-950 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-32 sm:py-40">
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-bold text-amber-300 mb-10 text-center"
          style={line(0)}
        >
          {t("residencyInfoUi.meTooIntro")}
        </div>

        <div className="space-y-7 text-center">
          {ME_TOO_KEYS.map((key, i) => (
            <p
              key={i}
              data-testid={`me-too-line-${i + 1}`}
              className="font-serif italic text-slate-200 text-2xl sm:text-3xl leading-snug"
              style={line(200 + i * 500)}
            >
              &ldquo;{t(key)}&rdquo;
            </p>
          ))}
        </div>

        {/* Punchline — heavier, larger, slower reveal */}
        <p
          data-testid="me-too-punchline"
          className="mt-14 text-center font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-white"
          style={{
            opacity: on ? 1 : 0,
            transform: on ? "translateY(0) scale(1)" : "translateY(30px) scale(0.94)",
            filter: on ? "blur(0)" : "blur(6px)",
            transition: `opacity 1200ms ease ${punchDelay}ms, transform 1200ms cubic-bezier(0.16, 1, 0.3, 1) ${punchDelay}ms, filter 1200ms ease ${punchDelay}ms`,
          }}
        >
          {t(PUNCHLINE_KEY)}
        </p>

        {/* Amber divider — expands after punchline settles */}
        <div className="mt-10 flex justify-center">
          <div
            className="h-px bg-amber-400"
            style={{
              width: on ? "72px" : "0px",
              transition: `width 800ms cubic-bezier(0.65, 0, 0.35, 1) ${punchDelay + 900}ms`,
            }}
          />
        </div>

        <p
          data-testid="me-too-framing"
          className="mt-8 max-w-2xl mx-auto text-center text-base sm:text-lg text-slate-400 leading-relaxed"
          style={{
            opacity: on ? 1 : 0,
            transform: on ? "translateY(0)" : "translateY(12px)",
            transition: `opacity 900ms ease ${punchDelay + 1600}ms, transform 900ms ease ${punchDelay + 1600}ms`,
          }}
        >
          {t("residencyInfoUi.meTooFraming")}
        </p>
      </div>
    </section>
  );
}

export default function ResidencyInfo() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMember = user?.tier === "premium" || user?.role === "admin";
  const applyHref = (id) => isMember ? `/residency/apply?program=${id}` : "/pricing?highlight=residency";

  return (
    <div data-testid="residency-info" className="bg-white">
      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_20%,rgba(30,64,175,0.55),transparent_60%),radial-gradient(circle_at_80%_80%,rgba(245,158,11,0.35),transparent_55%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">
              <FlaskConical size={11} /> {t("residencyInfoUi.heroBadge")}
            </div>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
              {t("residencyInfoUi.heroTitle1")}<br />
              <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">{t("residencyInfoUi.heroTitle2")}</span>
            </h1>
            <p className="mt-6 font-serif italic text-xl text-slate-200 leading-relaxed max-w-2xl">
              {t("residencyInfoUi.heroItalic")}
            </p>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed max-w-2xl">
              {t("residencyInfoUi.heroBody")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {isMember ? (
                <Link to="/residency/apply">
                  <Button data-testid="residency-hero-apply" size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-7 rounded-full">
                    {t("residencyInfoUi.heroApplyButton")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              ) : (
                <Link to="/pricing?highlight=residency">
                  <Button data-testid="residency-hero-unlock" size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-7 rounded-full">
                    <Lock size={14} className="mr-2" /> {t("residencyInfoUi.heroUnlockButton")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── ME TOO · cinematic mirror reveal ─────────────────────── */}
      <MeTooReveal />

      {/* ─── WALK-THROUGH ON THE PROFESSOR'S CHART ────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
          <div className="max-w-3xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-3">{t("residencyInfoUi.walkThroughKicker")}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
              {t("residencyInfoUi.walkThroughTitle")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t("residencyInfoUi.walkThroughBody")}
            </p>
          </div>
          <BeforeAfterAnnotated
            before={{
              src: CHART_BEFORE,
              alt: "The Professor's XAUUSD 1h chart — before, with the setup framed",
              spots: CHART_STEPS_BEFORE.map((s) => ({ ...s, title: t(s.titleKey), body: t(s.bodyKey) })),
              masks: BEFORE_MASK,
              caption: t("residencyInfoUi.chartBeforeCaption"),
            }}
            after={{
              src: CHART_AFTER,
              alt: "The Professor's XAUUSD 1h chart — after, showing the full move",
              spots: CHART_STEPS_AFTER.map((s) => ({ ...s, title: t(s.titleKey), body: t(s.bodyKey) })),
              caption: t("residencyInfoUi.chartAfterCaption"),
            }}
          />
        </div>
      </section>

      {/* ─── WHAT SIX DAYS DOES TO YOU ────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-3">{t("residencyInfoUi.sixDaysKicker")}</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
            {t("residencyInfoUi.sixDaysTitle")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { dayKey: "residencyInfoUi.day2Label", titleKey: "residencyInfoUi.day2Title", bodyKey: "residencyInfoUi.day2Body" },
            { dayKey: "residencyInfoUi.day4Label", titleKey: "residencyInfoUi.day4Title", bodyKey: "residencyInfoUi.day4Body" },
            { dayKey: "residencyInfoUi.day6Label", titleKey: "residencyInfoUi.day6Title", bodyKey: "residencyInfoUi.day6Body" },
          ].map((p) => (
            <div key={p.dayKey} className="rounded-2xl border border-slate-200/80 p-8 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_36px_-16px_rgba(30,64,175,0.18)] transition-all duration-300">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">{t(p.dayKey)}</div>
              <h3 className="mt-3 font-display font-bold text-2xl text-slate-900 leading-snug tracking-tight">{t(p.titleKey)}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t(p.bodyKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── THREE WAYS IN — compact strip ────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-blue-700 font-bold mb-3">{t("residencyInfoUi.threeWaysKicker")}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tighter leading-tight">
              {t("residencyInfoUi.threeWaysTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {FORMATS.map((f) => (
              <Link
                key={f.id}
                to={applyHref(f.id)}
                data-testid={`residency-card-${f.id}`}
                className="group flex items-start gap-4 bg-white border border-slate-200/80 rounded-2xl p-6 hover:border-blue-300 hover:-translate-y-1 hover:shadow-[0_16px_36px_-16px_rgba(30,64,175,0.18)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-800/10 text-blue-800 flex items-center justify-center shrink-0 group-hover:bg-blue-800 group-hover:text-white transition-colors">
                  <f.Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg text-slate-900 tracking-tight">{t(f.labelKey)}</div>
                  <div className="mt-1 text-sm text-slate-600 leading-snug">{t(f.lineKey)}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-800 group-hover:text-blue-700">
                    {isMember ? <>{t("residencyInfoUi.formatApplyLink")} <ArrowRight size={12} /></> : <><Lock size={11} /> {t("residencyInfoUi.formatMembersOnlyLink")}</>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FULL MEMBER STORIES ──────────────────────────────────── */}
      <ResidencyCaseStudies />

      {/* ─── FINAL INVITATION ─────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-24">
        <div className="rounded-3xl bg-slate-950 text-white p-12 sm:p-16 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
          <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-bold mb-4">{t("residencyInfoUi.finalKicker")}</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl leading-[1.05] tracking-tighter">
                {t("residencyInfoUi.finalTitle")}
              </h2>
              <p className="mt-4 text-slate-300 max-w-md">
                {t("residencyInfoUi.finalBody")}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              {isMember ? (
                <Link to="/residency/apply">
                  <Button data-testid="residency-final-apply" size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 rounded-full">
                    {t("residencyInfoUi.finalApplyButton")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              ) : (
                <Link to="/pricing?highlight=residency">
                  <Button data-testid="residency-final-unlock" size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8 rounded-full">
                    <Lock size={14} className="mr-2" /> {t("residencyInfoUi.finalUnlockButton")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* ─── BRAND SIGN-OFF · plays on "Peak" ─────────────────────── */}
      <PeakSignOff isMember={isMember} />
    </div>
  );
}

/**
 * Final tagline sign-off. Ties back to the broader Peak Market Society
 * journey with a horizontal "climb rail" (Foundations → Curriculum →
 * Ascent → Residency → Peak) — the last waypoint pulses in amber, the
 * rail draws itself in on scroll, and the copy directly answers the
 * "me too" mirror above ("almost there" → "the last stretch"). Designed
 * to punctuate the page like a signature.
 */
function PeakSignOff({ isMember }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // The visitor's actual journey through the platform — the last stop
  // is The Peak, which isn't a page, it's the outcome.
  const STAGES = [
    { labelKey: "residencyInfoUi.stageFoundationsLabel",  subKey: "residencyInfoUi.stageFoundationsSub" },
    { labelKey: "residencyInfoUi.stageCurriculumLabel",   subKey: "residencyInfoUi.stageCurriculumSub" },
    { labelKey: "residencyInfoUi.stageAscentLabel",   subKey: "residencyInfoUi.stageAscentSub" },
    { labelKey: "residencyInfoUi.stageResidencyLabel", subKey: "residencyInfoUi.stageResidencySub" },
    { labelKey: "residencyInfoUi.stagePeakLabel",     subKey: "residencyInfoUi.stagePeakSub" },
  ];
  const N = STAGES.length;

  return (
    <section
      ref={ref}
      data-testid="residency-peak-signoff"
      className="relative overflow-hidden bg-white py-28 sm:py-36 border-t border-slate-200/60"
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(37,99,235,0.06),transparent_65%)]" />
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-bold text-blue-700 mb-10"
          style={{ opacity: on ? 1 : 0, transition: "opacity 700ms ease 0ms" }}
        >
          {t("residencyInfoUi.signoffEyebrow")}
        </div>

        {/* Climb rail */}
        <div
          data-testid="residency-climb-rail"
          className="relative max-w-3xl mx-auto mb-14"
          aria-label={t("residencyInfoUi.climbRailAriaLabel")}
        >
          {/* Rail base line */}
          <div className="absolute left-[5%] right-[5%] top-[10px] h-[2px] bg-slate-200 rounded-full" />
          {/* Rail progress (draws in) */}
          <div
            className="absolute left-[5%] top-[10px] h-[2px] bg-gradient-to-r from-blue-800 via-blue-600 to-amber-500 rounded-full"
            style={{
              width: on ? "90%" : "0%",
              transition: "width 1400ms cubic-bezier(0.65,0,0.35,1) 300ms",
            }}
          />
          {/* Waypoints */}
          <div className="relative flex justify-between">
            {STAGES.map((stage, i) => {
              const isPeak = i === N - 1;
              const isResidency = i === N - 2;
              // Each waypoint dot appears as the rail passes it
              const dotDelay = 300 + (i / (N - 1)) * 1300;
              return (
                <div key={stage.labelKey} className="flex flex-col items-center w-24" style={{ marginTop: "-4px" }}>
                  <div className="relative flex items-center justify-center">
                    {isPeak && on && (
                      <span className="absolute w-9 h-9 rounded-full bg-amber-500/30 animate-ping" aria-hidden />
                    )}
                    <div
                      className={`relative rounded-full border-2 flex items-center justify-center ${
                        isPeak
                          ? "w-7 h-7 bg-amber-500 border-white shadow-[0_4px_14px_rgba(245,158,11,0.55)]"
                          : isResidency
                          ? "w-6 h-6 bg-blue-800 border-white shadow-[0_2px_8px_rgba(30,64,175,0.35)]"
                          : "w-5 h-5 bg-white border-blue-800"
                      }`}
                      style={{
                        opacity: on ? 1 : 0,
                        transform: on ? "scale(1)" : "scale(0.4)",
                        transition: `opacity 500ms ease ${dotDelay}ms, transform 500ms cubic-bezier(0.16,1,0.3,1) ${dotDelay}ms`,
                      }}
                    />
                  </div>
                  <div
                    className={`mt-3 text-[11px] sm:text-xs font-display font-bold tracking-tight text-center ${
                      isPeak ? "text-amber-600" : isResidency ? "text-blue-800" : "text-slate-700"
                    }`}
                    style={{
                      opacity: on ? 1 : 0,
                      transform: on ? "translateY(0)" : "translateY(6px)",
                      transition: `opacity 500ms ease ${dotDelay + 200}ms, transform 500ms ease ${dotDelay + 200}ms`,
                    }}
                  >
                    {t(stage.labelKey)}
                  </div>
                  <div
                    className="mt-0.5 text-[10px] text-slate-400 hidden sm:block"
                    style={{
                      opacity: on ? 1 : 0,
                      transition: `opacity 500ms ease ${dotDelay + 350}ms`,
                    }}
                  >
                    {t(stage.subKey)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <h2
          className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-slate-900"
          style={{
            opacity: on ? 1 : 0,
            transform: on ? "translateY(0)" : "translateY(24px)",
            filter: on ? "blur(0)" : "blur(6px)",
            transition: "opacity 1100ms ease 1700ms, transform 1100ms cubic-bezier(0.16,1,0.3,1) 1700ms, filter 1100ms ease 1700ms",
          }}
        >
          {t("residencyInfoUi.signOffTitle1")}{" "}
          <span className="bg-gradient-to-r from-blue-800 via-blue-700 to-amber-500 bg-clip-text text-transparent">{t("residencyInfoUi.signOffTitle2")}</span>?
        </h2>

        <p
          className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto"
          style={{
            opacity: on ? 1 : 0,
            transform: on ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 900ms ease 2300ms, transform 900ms ease 2300ms",
          }}
        >
          {t("residencyInfoUi.signOffBody")}
        </p>

        <div
          className="mt-10"
          style={{
            opacity: on ? 1 : 0,
            transform: on ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 900ms ease 2700ms, transform 900ms ease 2700ms",
          }}
        >
          {isMember ? (
            <Link to="/residency/apply">
              <Button data-testid="residency-signoff-apply" size="lg" className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12 px-8 rounded-full">
                {t("residencyInfoUi.signOffApplyButton")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          ) : (
            <Link to="/pricing?highlight=residency">
              <Button data-testid="residency-signoff-unlock" size="lg" className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12 px-8 rounded-full">
                <Lock size={14} className="mr-2" /> {t("residencyInfoUi.signOffUnlockButton")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
